"use client";

import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import {
  createClient,
  signOut as signOutUser,
  getSession,
  getLinkedIdentities,
  getPrimaryIdentity,
} from "@/auth/client";
import type { User, Provider, UserIdentity, Session } from "@supabase/supabase-js";
import { USE_MOCK_AUTH } from "@/config";
import { createMockUser } from "@/auth/mock-user";

type AuthContextType = {
  user: User | null;

  isLoading: boolean;
  isInitialized: boolean;
  identities: UserIdentity[];
  primaryIdentity: UserIdentity | null;
  hasLinkedIdentity: (provider: Provider) => boolean;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<boolean>;
  sessionError: string | null;
  isSessionExpired: boolean;

};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to check if session is expired
const isSessionExpired = (session: Session | null): boolean => {
  if (!session?.expires_at) return true;
  const expiresAt = new Date(session.expires_at * 1000);
  return expiresAt <= new Date();
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [identities, setIdentities] = useState<UserIdentity[]>([]);
  const [primaryIdentity, setPrimaryIdentity] = useState<UserIdentity | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [isSessionExpiredState, setIsSessionExpiredState] = useState(false);
  
  const router = useRouter();
  const supabase = createClient();
  const isMounted = useRef(true);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const authListener = useRef<{ unsubscribe: () => void } | null>(null);

  // Clear any pending timeouts and listeners when component unmounts
  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      if (authListener.current) {
        authListener.current.unsubscribe();
        authListener.current = null;
      }
    };
  }, []);

  const clearAuthState = useCallback(() => {
    setUser(null);
    setIdentities([]);
    setPrimaryIdentity(null);
    setSessionError(null);
    setIsSessionExpiredState(false);
  }, []);

  const updateAuthState = useCallback(async (session: Session | null) => {
    if (!isMounted.current) return;
    
    // Clear any existing refresh timeout when updating auth state
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
    
    const sessionUser = session?.user || null;
    setUser(sessionUser);
    
    if (sessionUser) {
      try {
        const linkedIdentities = getLinkedIdentities(sessionUser);
        const primary = getPrimaryIdentity(sessionUser);
        
        setIdentities(linkedIdentities);
        setPrimaryIdentity(primary);
        setSessionError(null);
        setIsSessionExpiredState(false);
        
        // Set up session refresh before it expires
        if (session?.expires_at) {
          const expiresAt = new Date(session.expires_at * 1000);
          const now = new Date();
          const timeUntilExpiry = expiresAt.getTime() - now.getTime();
          
          // Only refresh if we're within the last hour of the session and not already refreshing
          if (timeUntilExpiry < 60 * 60 * 1000 && timeUntilExpiry > 0) {
            // Refresh 5 minutes before expiry, but at least 10 seconds from now
            const refreshTime = Math.max(timeUntilExpiry - (5 * 60 * 1000), 10000);
            refreshTimeoutRef.current = setTimeout(async () => {
              if (isMounted.current) {
                console.log('[Auth] Auto-refreshing session before expiry');
                try {
                  const { data: { session: newSession } } = await supabase.auth.getSession();
                  if (newSession) {
                    await updateAuthState(newSession);
                  }
                } catch (error) {
                  console.error('[Auth] Error auto-refreshing session:', error);
                }
              }
            }, refreshTime);
          }
        }
      } catch (error) {
        console.error('Error updating auth state:', error);
        setSessionError('Failed to update authentication state');
      }
    } else {
      setUser(null);
      setIdentities([]);
      setPrimaryIdentity(null);
      setSessionError(null);
      setIsSessionExpiredState(false);
    }
  }, [supabase]);

  const refreshUser = useCallback(async (): Promise<boolean> => {
    if (!isMounted.current) return false;
    
    // Prevent multiple simultaneous refresh attempts
    if (isLoading) {
      console.log('[Auth] Refresh already in progress, skipping duplicate');
      return !!user; // Return current auth state
    }
    
    try {
      console.log('[Auth] Refreshing user session...');
      setIsLoading(true);

      // In development with mock auth, immediately use mock user
      if (USE_MOCK_AUTH) {
        const mockUser = createMockUser();
        await updateAuthState({
          user: mockUser as unknown as User,
          expires_at: Math.floor(Date.now() / 1000) + (60 * 60 * 24 * 365),
        } as unknown as Session);
        return true;
      }
      
      // Get the current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('[Auth] Error getting session:', sessionError);
        throw sessionError;
      }
      
      // Only log if we have a session and the user has changed
      if (session?.user?.email !== user?.email) {
        console.log('[Auth] Session refreshed for user:', session?.user?.email || 'No active session');
      }
      
      // Update auth state with the new session
      await updateAuthState(session);
      
      // If we have a session but it's expired, mark it as such
      if (session && isSessionExpired(session)) {
        console.log('[Auth] Session expired');
        setIsSessionExpiredState(true);
        setSessionError('Your session has expired. Please sign in again.');
        return false;
      }
      
      return !!session?.user;
    } catch (error) {
      console.error('[Auth] Error in refreshUser:', error);
      setSessionError('Failed to refresh session');
      return false;
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
        setIsInitialized(true);
      }
    }
  }, [supabase, updateAuthState, isLoading, user]);

  // Initialize auth state on mount
  useEffect(() => {
    let mounted = true;
    
    const initializeAuth = async () => {
      if (!mounted) return;
      
      try {
        console.log('[Auth] Initializing authentication...');
        
        // In development with mock auth, initialize immediately with mock user
        if (USE_MOCK_AUTH) {
          const mockUser = createMockUser();
          console.log('[Auth] Using mock authentication with user ID:', mockUser.id);
          setUser(mockUser as unknown as User);
          setIdentities([]);
          setPrimaryIdentity(null);
          setSessionError(null);
          setIsSessionExpiredState(false);
          if (mounted) {
            setIsLoading(false);
            setIsInitialized(true);
          }
          return;
        }
        
        // Get initial session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('[Auth] Error getting initial session:', sessionError);
          setSessionError('Failed to get session');
        } else {
          console.log('[Auth] Initial session:', session?.user?.email || 'No session');
          await updateAuthState(session);
        }
        
        // Set up auth state listener only once
        if (!authListener.current && mounted) {
          const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, session) => {
              if (!mounted) return;
              
              console.log(`[Auth] Auth state changed: ${event}`, { 
                user: session?.user?.email || 'No user'
              });
              
              if (event === 'SIGNED_OUT') {
                setUser(null);
                setIdentities([]);
                setPrimaryIdentity(null);
                setSessionError(null);
                setIsSessionExpiredState(false);
                if (typeof window === 'undefined' || window.location.pathname !== '/') {
                  router.push('/');
                }
              } else if (session) {
                await updateAuthState(session);
              }
            }
          );
          
          authListener.current = subscription;
        }
      } catch (error) {
        console.error('[Auth] Error initializing auth:', error);
        if (mounted) {
          setSessionError('Failed to initialize authentication');
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
          setIsInitialized(true);
          console.log('[Auth] Authentication initialization complete');
        }
      }
    };
    
    // Small delay to prevent hydration issues
    const timer = setTimeout(initializeAuth, 100);
    
    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, []); // Remove all dependencies to prevent re-initialization

  const hasLinkedIdentity = useCallback((provider: Provider) => {
    return identities.some(id => id.provider === provider);
  }, [identities]);

  const signOut = useCallback(async () => {
    try {
      await signOutUser();
      setUser(null);
      setIdentities([]);
      setPrimaryIdentity(null);
      setSessionError(null);
      setIsSessionExpiredState(false);
      if (typeof window === 'undefined' || window.location.pathname !== '/') {
        router.push('/');
      }
    } catch (error) {
      console.error('Error signing out:', error);
      setSessionError('Failed to sign out');
    }
  }, [router]);

  const value = {
    user,
    isLoading,
    isInitialized,
    identities,
    primaryIdentity,
    hasLinkedIdentity,
    signOut,
    refreshUser,
    sessionError,
    isSessionExpired: isSessionExpiredState,


  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
